import { OnResultDocument, useCreateGroupMutation, useTagResourceMutation } from "../generated";
import { useApolloClient } from "@apollo/client";
import useNewrelic from "./useNewrelic";
// import useSnackBars from "./useSnackbar";
// import { useState } from "react";

const useTagResource = () => {
  const { noticeError } = useNewrelic();
  const [createGroupMutation] = useCreateGroupMutation();
  const [tagResourceMutation] = useTagResourceMutation();
  
  const client = useApolloClient();
  // const { setAlerts } = useSnackBars();

  const tagResource = async (
    arn: string,
    resourcesSelected: string[],
    tagObject: Record<string, string>,
  ) => {
    const tagsString = Object.entries(tagObject)
			.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join(";");

    try {
      const { data } = await tagResourceMutation({
        variables: {
          arn,
          resources: resourcesSelected,
          tags: tagsString,
        },
        context: {
          apiName: "resource_explorer",
        },
        notifyOnNetworkStatusChange: true,
      });

      const tagId = data?.tagResource?.id;
      if (!tagId) throw new Error("Missing tag operation ID");

			const subscriptionResult = await new Promise<{
				status: string;
        result: any;
   	 	}>((resolve, reject) => {
        const observable = client?.subscribe({
					query: OnResultDocument,
					variables: { id: tagId },
        });
        const subscription = observable?.subscribe({
					next: ({ data }) => {
						const publishResult = data?.onResult;
						if (!publishResult) {
							reject(new Error("No publish result received"));
							subscription?.unsubscribe();
							return;
						}

						try {
							const parsedResult = JSON.parse(publishResult.result);
							resolve({
								status: publishResult.status,
								result: parsedResult,
							});
						} catch (err) {
							reject(new Error("Failed to parse result JSON"));
						}

						subscription?.unsubscribe();
					},
					error: (err) => {
            reject(new Error("Error in subscription"));
            subscription?.unsubscribe();
					},
        });
  	  });
			return {
				success: subscriptionResult.status === "COMPLETE",
				result: subscriptionResult.result,
			};
    } catch (error: any) {
      noticeError(error.message, "useTagResource");
      return { success: false, error: error.message };
    }
  };

  const createGroupAndTagResources = async (
    arn: string,
    groupname: string,
    tags: { key: string; value: string }[],
    regionsSelected: string[],
    resourcesSelected: string[],
    setAlerts: React.Dispatch<React.SetStateAction<{
      severity: "error" | "success" | "info" | "warning";
      msg: string | string[];
    }[]>>
  ): Promise<boolean> => {
    const tagFilters = tags.map((tag) => ({
      Key: tag.key,
      Values: [tag.value],
    }));
    const tagObject = tags.reduce((acc, tag) => {
      acc[tag.key] = tag.value;
      return acc;
    }, {} as Record<string, string>);

    const chunkArray = <T,>(array: T[], chunkSize: number): T[][] => {
      const chunks: T[][] = [];
      for (let i = 0; i < array.length; i += chunkSize) {
        chunks.push(array.slice(i, i + chunkSize));
      }
      return chunks;
    };
    const regionChunks = chunkArray(regionsSelected, 1);
    const resourceChunks = chunkArray(resourcesSelected, 500);

    // Step 1: Create group in all region chunks
    const groupResults = await Promise.all(regionChunks.map((regionChunk) =>
      createGroupMutation({
        variables: {
          arn,
          groupname,
          description: "Resource Group Created by 6pillars",
          tags: tagFilters,
          regions: regionChunk,
        },
        context: { apiName: "resource_explorer" },
      })
    ));

    let groupCreationSuccess = true;

    for (const response of groupResults) {
      const groupResult = response?.data?.createGroup;
      const status = groupResult?.status;

      if (status !== "COMPLETE") {
        groupCreationSuccess = false;

        try {
          const regionErrors = JSON.parse(groupResult?.regions || "{}");
          const alreadyExists = Object.values(regionErrors).some(
            (msg) => typeof msg === "string" && msg.includes("Group Already Exists")
          );
          setAlerts([
            {
              severity: "error",
              msg: alreadyExists
                ? "A group with the same name already exists in one or more regions."
                : "Failed to create group due to an unknown error.",
            },
          ]);
        } catch {
          setAlerts([
            {
              severity: "error",
              msg: "Failed to create group and could not read error details.",
            },
          ]);
        }
        break;
      }
    }

    // Step 2: Tag resources if group creation succeeded
    if (!groupCreationSuccess) return false;

    const tagResults = await Promise.all(resourceChunks.map((chunk) =>
      tagResource(arn, chunk, tagObject)
    ));
    let allTagsSucceeded = true;
    const failedResources: string[] = [];
    const failedResourceIds: string[] = [];
    for (const response of tagResults) {
      const status = response?.result?.status;
      if (status !== "COMPLETE") {
        allTagsSucceeded = false;
        break;
      }

      const failures = response.result?.FailedResourcesMap || {};
      for (const k in failures) {
        failedResourceIds.push(k);
        failedResources.push(` ${k} - ${failures[k]["ErrorMessage"]} `);
      }
    }
    const totalSelected = resourcesSelected.length;
    const totalFailed = failedResourceIds.length;
    const totalPassed = totalSelected - totalFailed;
    if (allTagsSucceeded && failedResources.length === 0) {
      setAlerts([
        {
          severity: "success",
          msg: `${totalPassed} resource(s) tagged and group created successfully.`,
        },
      ]);
    } else {
      allTagsSucceeded = false;
      setAlerts([
        {
          severity: "success",
          msg: `Group created successfully. ${totalPassed} resource(s) tagged.`,
        },
        {
          severity: "warning",
          msg: [
            `${totalFailed} resource(s) failed to tag:`,
            ...failedResources,
          ],
        },
      ]);
    }
    return allTagsSucceeded;
  };

  return { createGroupAndTagResources };
};

export { useTagResource };
